import apiClient from './client';

export interface Country {
  id: number;
  iso_code: string;
  name: string;
  brave_country_code: string;
  region: string;
  currency_code: string;
  flag_emoji: string;
  display_order: number;
  is_active: boolean;
  metrics_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CountryCreateRequest {
  iso_code: string;
  name: string;
  brave_country_code: string;
  region: string;
  currency_code: string;
  flag_emoji: string;
  display_order?: number;
  is_active?: boolean;
}

export interface CountryUpdateRequest {
  name?: string;
  brave_country_code?: string;
  region?: string;
  currency_code?: string;
  flag_emoji?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface CountryListParams {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  region?: string;
  is_active?: boolean;
}

export interface CountryListResponse {
  countries: Country[];
  total_count: number;
  page_count: number;
}

// Prompt interfaces
export interface LLMPrompt {
  id: string;
  prompt_type: string;
  prompt_name: string;
  prompt_text: string;
  is_active: boolean;
  version: number;
  search_method?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  metadata?: any;
}

export interface LLMPromptHistory {
  id: string;
  prompt_id: string;
  prompt_type: string;
  prompt_name: string;
  prompt_text: string;
  version: number;
  action: string;
  changed_by: string;
  change_reason?: string;
  created_at: string;
  metadata?: any;
}

export interface CreatePromptRequest {
  prompt_type: string;
  prompt_name: string;
  prompt_text: string;
  search_method?: string;
  metadata?: any;
}

export interface UpdatePromptRequest {
  prompt_name?: string;
  prompt_text?: string;
  metadata?: any;
}

export interface PromptValidation {
  is_valid: boolean;
  found_variables: string[];
  errors?: string[];
}

export interface PromptTestResult {
  success: boolean;
  output?: string;
  processed_text: string;
  variables: Record<string, any>;
  error?: string;
}

export interface ValidatePromptRequest {
  prompt_text: string;
  required_variables?: string[];
}

export interface TestPromptRequest {
  prompt_text: string;
  sample_data: Record<string, any>;
}

export interface RollbackPromptRequest {
  target_version: number;
}

/**
 * Administration API methods
 */
class AdministrationApi {
  private basePath = '';

  /**
   * Get all countries with optional filtering
   */
  async getCountries(params?: CountryListParams): Promise<CountryListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.region) searchParams.append('region', params.region);
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());

    const query = searchParams.toString();
    const url = query ? `${this.basePath}/countries?${query}` : `${this.basePath}/countries`;
    
    const response = await apiClient.getRaw<Country[]>(url);
    
    return {
      countries: response.data,
      total_count: parseInt(response.meta?.total_count?.toString() || '0'),
      page_count: parseInt(response.meta?.page_count?.toString() || '1'),
    };
  }

  /**
   * Get country by ID
   */
  async getCountry(id: number): Promise<Country> {
    return apiClient.get<Country>(`${this.basePath}/countries/${id}`);
  }

  /**
   * Create new country
   */
  async createCountry(data: CountryCreateRequest): Promise<Country> {
    return apiClient.post<Country>(`${this.basePath}/countries`, data);
  }

  /**
   * Update existing country
   */
  async updateCountry(id: number, data: CountryUpdateRequest): Promise<Country> {
    return apiClient.patch<Country>(`${this.basePath}/countries/${id}`, data);
  }

  /**
   * Delete country
   */
  async deleteCountry(id: number): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/countries/${id}`);
  }

  /**
   * Get regions list for dropdown
   */
  async getRegions(): Promise<string[]> {
    return apiClient.get<string[]>(`${this.basePath}/countries/regions`);
  }

  /**
   * Get currencies list for dropdown
   */
  async getCurrencies(): Promise<string[]> {
    return apiClient.get<string[]>(`${this.basePath}/countries/currencies`);
  }

  // Prompt API methods
  
  /**
   * Get all prompts with optional filtering
   */
  async getPrompts(type?: string, activeOnly?: boolean): Promise<LLMPrompt[]> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
    
    const query = params.toString();
    const url = query ? `${this.basePath}/prompts?${query}` : `${this.basePath}/prompts`;
    
    return apiClient.get<LLMPrompt[]>(url);
  }

  /**
   * Get prompt by ID
   */
  async getPrompt(id: string): Promise<LLMPrompt> {
    return apiClient.get<LLMPrompt>(`${this.basePath}/prompts/${id}`);
  }

  /**
   * Create new prompt
   */
  async createPrompt(data: CreatePromptRequest): Promise<LLMPrompt> {
    return apiClient.post<LLMPrompt>(`${this.basePath}/prompts`, data);
  }

  /**
   * Update existing prompt
   */
  async updatePrompt(id: string, data: UpdatePromptRequest): Promise<LLMPrompt> {
    return apiClient.put<LLMPrompt>(`${this.basePath}/prompts/${id}`, data);
  }

  /**
   * Delete prompt
   */
  async deletePrompt(id: string): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/prompts/${id}`);
  }

  /**
   * Activate prompt
   */
  async activatePrompt(id: string): Promise<void> {
    return apiClient.post<void>(`${this.basePath}/prompts/${id}/activate`, {});
  }

  /**
   * Deactivate prompt
   */
  async deactivatePrompt(id: string): Promise<void> {
    return apiClient.post<void>(`${this.basePath}/prompts/${id}/deactivate`, {});
  }

  /**
   * Get prompt history
   */
  async getPromptHistory(id: string): Promise<LLMPromptHistory[]> {
    return apiClient.get<LLMPromptHistory[]>(`${this.basePath}/prompts/${id}/history`);
  }

  /**
   * Validate prompt template
   */
  async validatePrompt(data: ValidatePromptRequest): Promise<PromptValidation> {
    return apiClient.post<PromptValidation>(`${this.basePath}/prompts/validate`, data);
  }

  /**
   * Test prompt with sample data
   */
  async testPrompt(data: TestPromptRequest): Promise<PromptTestResult> {
    return apiClient.post<PromptTestResult>(`${this.basePath}/prompts/test`, data);
  }

  /**
   * Rollback prompt to previous version
   */
  async rollbackPrompt(id: string, data: RollbackPromptRequest): Promise<LLMPrompt> {
    return apiClient.post<LLMPrompt>(`${this.basePath}/prompts/${id}/rollback`, data);
  }
}

// Export singleton instance
const administrationApi = new AdministrationApi();
export default administrationApi;